import { AppMenuItem } from '@/core/permissions/permission.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

/**
 * Segmentos de rota do módulo de Médicos — fonte única e tipada (`as const`).
 * Rotas, breadcrumbs, menu e `routerLink` derivam daqui, evitando strings mágicas
 * espalhadas; renomear um caminho passa a ser uma única alteração.
 */
export const MEDICOS_ROUTES = {
    ROOT: 'medicos',
    DASHBOARD: '',
    ITEM_BASE: 'item',
    ITEM: {
        DADOS_REGISTRO: 'dados-registro',
        BIOMETRIA: 'biometria',
        CONTATOS: 'contatos',
        DOCUMENTOS: 'documentos',
        FORMACAO: 'formacao',
        ESPECIALIDADE: 'especialidade',
        HISTORICO: 'historico',
        FUNCOES: 'funcoes',
        ANOTACOES: 'anotacoes',
        CONSULTORIO_PRIVADO: 'consultorio-privado',
        TELEMEDICINA: 'telemedicina',
        PCMSO: 'pcmso',
        CONFLITO_INTERESSE: 'conflito-interesse',
        VISTO_TEMPORARIO_LOCALIZAR: 'visto-temporario-localizar',
        VISTO_TEMPORARIO_NOVO: 'visto-temporario-novo',
        CT_SOLICITAR: 'central-transferencias-solicitar',
        CT_LOCALIZAR_CPF: 'central-transferencias-localizar-cpf',
        CT_ABERTAS_UF_ORIGEM: 'central-transferencias-abertas-uf-origem',
        CT_ABERTAS_UF_DESTINO: 'central-transferencias-abertas-uf-destino',
        CT_AGUARDANDO_ENCERRAMENTO: 'central-transferencias-aguardando-encerramento',
        DOCUMENTOS_EMISSAO: 'documentos-emissao'
    },
    RELATORIOS: {
        ROOT: 'relatorios',
        CONTROLE_ADMINISTRATIVO: 'controle-administrativo',
        CERTIFICADO_REGULARIDADE: 'certificado-regularidade',
        OFICIO_CONFIRMACAO: 'oficio-confirmacao',
        OFICIO_REPUDIO: 'oficio-repudio',
        RELATORIO_SELECAO: 'relatorio-selecao',
        CERTIDAO_CANCELAMENTO: 'certidao-cancelamento-medico',
        CERTIDAO_JUBILAMENTO: 'certidao-jubilamento-medico',
        CERTIDAO_FINS_INTERNACIONAIS: 'certidao-fins-internacionais-medico',
        CERTIDAO_EME: 'certidao-eme-medico',
        CERTIDAO_CADASTRAL: 'certidao-cadastral-medico',
        CERTIDAO_INSCRICAO: 'certidao-inscricao-medico',
        CERTIDAO_QUITACAO_DEBITO: 'certidao-quitacao-debito-medico',
        FICHA_CADASTRAL: 'ficha-cadastral-medico',
        FICHA_CADASTRAL_RESUMIDA: 'ficha-cadastral-resumida-medico',
        FICHA_CADASTRAL_LGPD: 'ficha-cadastral-lgpd-medico',
        TERMO_CIENCIA_COMPROMISSO: 'termo-ciencia-compromisso-medico',
        DECLARACAO_RESP_PAGAMENTO: 'declaracao-resp-pagamento-medico',
        DECLARACAO_MEDICO_CONVOCADO: 'declaracao-medico-convocado-medico',
        OFICIO_MEDICO: 'oficio-medico',
        OFICIO_CANCELAMENTO_INC_SECUNDARIA: 'oficio-confirmacao-cancelamento-inc-secundaria-medico',
        REQUERIMENTOS_DIVERSOS: 'requerimentos-diversos-medico',
        DECLARACAO_EXTERIOR: 'declaracao-exterior-medico',
        REQUERIMENTO_UNICO_MEDICO: 'requerimento-unico-medico',
        ETIQUETA_ENDERECO_MEDICO: 'etiqueta-endereco-medico',
        ETIQUETA_PROFISSIONAL: 'etiqueta-profissional'
    }
} as const;

export interface MedicosMenuActions {
    pesquisarPessoa: () => void;
}

/**
 * Menu lateral do módulo Médicos. As folhas usam `.escopo` (qualquer role da
 * funcionalidade libera) para concordar com o gate das rotas — ver medicos-menu-rota.spec.
 */
export function createMedicosMenu(actions: MedicosMenuActions): AppMenuItem[] {
    const item = `${MEDICOS_ROUTES.ROOT}/${MEDICOS_ROUTES.ITEM_BASE}`;
    const P = PERMISSOES.medicos;
    const I = MEDICOS_ROUTES.ITEM;
    return [
        {
            label: 'Home',
            expanded: true,
            items: [
                {
                    label: 'Dados do Médico',
                    expanded: true,
                    items: [
                        { label: 'Dados de Registro', icon: 'pi pi-fw pi-user-edit', routerLink: [item, I.DADOS_REGISTRO], fragment: 'Dados de Registro', data: { breadcrumb: 'Dados de Registro' }, permission: P.dadosRegistro.escopo },
                        { label: 'Biometria', icon: 'pi pi-fw pi-lock', routerLink: [item, I.BIOMETRIA], fragment: 'Biometria', data: { breadcrumb: 'Biometria' }, permission: P.biometria.escopo },
                        { label: 'Contatos', icon: 'pi pi-fw pi-phone', routerLink: [item, I.CONTATOS], fragment: 'Contatos', data: { breadcrumb: 'Contatos' }, permission: P.contato.escopo },
                        { label: 'Documentos', icon: 'pi pi-fw pi-file', routerLink: [item, I.DOCUMENTOS], fragment: 'Documentos', data: { breadcrumb: 'Documentos' }, permission: P.documentos.escopo },
                        { label: 'Formação', icon: 'pi pi-fw pi-graduation-cap', routerLink: [item, I.FORMACAO], fragment: 'Formação', data: { breadcrumb: 'Formação' }, permission: P.formacao.escopo },
                        { label: 'Especialidade', icon: 'pi pi-fw pi-star', routerLink: [item, I.ESPECIALIDADE], fragment: 'Especialidade', data: { breadcrumb: 'Especialidade' }, permission: P.especialidade.escopo },
                        { label: 'Histórico', icon: 'pi pi-fw pi-history', routerLink: [item, I.HISTORICO], fragment: 'Histórico', data: { breadcrumb: 'Histórico' }, permission: P.historico.escopo },
                        { label: 'Funções', icon: 'pi pi-fw pi-cog', routerLink: [item, I.FUNCOES], fragment: 'Funções', data: { breadcrumb: 'Funções' }, permission: P.funcoes.escopo },
                        { label: 'Anotações', icon: 'pi pi-fw pi-pencil', routerLink: [item, I.ANOTACOES], fragment: 'Anotações', data: { breadcrumb: 'Anotações' }, permission: P.anotacoes.escopo },
                        { label: 'Consultório Privado', icon: 'pi pi-fw pi-building', routerLink: [item, I.CONSULTORIO_PRIVADO], fragment: 'Consultório Privado', data: { breadcrumb: 'Consultório Privado' }, permission: P.consultorio.escopo },
                        { label: 'Telemedicina', icon: 'pi pi-fw pi-video', routerLink: [item, I.TELEMEDICINA], fragment: 'Telemedicina', data: { breadcrumb: 'Telemedicina' }, permission: P.telemedicina.escopo },
                        { label: 'PCMSO', icon: 'pi pi-fw pi-shield', routerLink: [item, I.PCMSO], fragment: 'PCMSO', data: { breadcrumb: 'PCMSO' }, permission: P.pcmso.escopo },
                        {
                            label: 'Conflito de Interesse',
                            icon: 'pi pi-fw pi-exclamation-triangle',
                            routerLink: [item, I.CONFLITO_INTERESSE],
                            fragment: 'Conflito de Interesse',
                            data: { breadcrumb: 'Conflito de Interesse' },
                            permission: P.conflitoInteresse.escopo
                        }
                    ]
                },
                {
                    label: 'Solicitações',
                    expanded: true,
                    items: [
                        {
                            label: 'Visto Temporário',
                            icon: 'pi pi-fw pi-id-card',
                            expanded: true,
                            items: [
                                { label: 'Localizar Visto', icon: 'pi pi-fw pi-search', routerLink: [item, I.VISTO_TEMPORARIO_LOCALIZAR], fragment: 'Localizar Visto', data: { breadcrumb: 'Localizar Visto' }, permission: P.vistoTemporario.escopo },
                                { label: 'Novo Visto', icon: 'pi pi-fw pi-plus-circle', routerLink: [item, I.VISTO_TEMPORARIO_NOVO], fragment: 'Novo Visto', data: { breadcrumb: 'Novo Visto' }, permission: P.vistoTemporario.escopo }
                            ]
                        },
                        {
                            label: 'Central de Transferências',
                            icon: 'pi pi-fw pi-send',
                            expanded: true,
                            items: [
                                { label: 'Solicitar', icon: 'pi pi-fw pi-plus-circle', routerLink: [item, I.CT_SOLICITAR], fragment: 'Solicitar', data: { breadcrumb: 'Solicitar' }, permission: P.centralTransferencia.escopo },
                                {
                                    label: 'Localizar Solicitação',
                                    icon: 'pi pi-fw pi-search',
                                    routerLink: [item, I.CT_LOCALIZAR_CPF],
                                    fragment: 'Localizar Solicitação',
                                    data: { breadcrumb: 'Localizar Solicitação' },
                                    permission: P.centralTransferencia.escopo
                                },
                                {
                                    label: 'Abertas UF Origem',
                                    icon: 'pi pi-fw pi-inbox',
                                    routerLink: [item, I.CT_ABERTAS_UF_ORIGEM],
                                    fragment: 'Abertas UF Origem',
                                    data: { breadcrumb: 'Abertas UF Origem' },
                                    permission: P.centralTransferencia.escopo
                                },
                                {
                                    label: 'Abertas por UF Destino',
                                    icon: 'pi pi-fw pi-map-marker',
                                    routerLink: [item, I.CT_ABERTAS_UF_DESTINO],
                                    fragment: 'Abertas por UF Destino',
                                    data: { breadcrumb: 'Abertas por UF Destino' },
                                    permission: P.centralTransferencia.escopo
                                },
                                {
                                    label: 'Aguardando Encerramento',
                                    icon: 'pi pi-fw pi-clock',
                                    routerLink: [item, I.CT_AGUARDANDO_ENCERRAMENTO],
                                    fragment: 'Aguardando Encerramento',
                                    data: { breadcrumb: 'Aguardando Encerramento' },
                                    permission: P.centralTransferencia.escopo
                                }
                            ]
                        },
                        {
                            label: 'Emissão VALID',
                            icon: 'pi pi-fw pi-id-card',
                            routerLink: [item, I.DOCUMENTOS_EMISSAO],
                            fragment: 'Emissão VALID',
                            data: { breadcrumb: 'Emissão VALID' },
                            permission: P.emissaoValid.escopo
                        }
                    ]
                },
                {
                    label: 'Pesquisar Pessoa',
                    icon: 'pi pi-fw pi-search',
                    command: actions.pesquisarPessoa
                },
                { label: 'Relatorios', icon: 'pi pi-fw pi-file-import', routerLink: [MEDICOS_ROUTES.ROOT + '/relatorios'], permission: P.relatorios.escopo }
            ]
        }
    ];
}
